// My Orders (web) — 1:1 mirror of the mobile Uber-style vertical shipping
// timeline (app/orders.tsx). Physical orders progress Ordered → Preparing →
// Shipped → Delivered; digital orders land instantly in the Library. Keeps the
// web buyer's tracking-link editor + auto-advance. Shared backend, untouched.
import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Check, Package, Truck, Home, Download, ChevronLeft, ShoppingBag, ExternalLink,
  Plus, Loader2, RefreshCw, CheckCircle2, RotateCcw, ArrowUpRight,
} from 'lucide-react';
import axios from 'axios';
import { FEATURES } from '@/config/features';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const C = {
  bg: '#E5EBDD', sageDeep: '#D9E1CB', cream: '#FAF6EC', creamWarm: '#F4EEDE',
  forest: '#1A4331', gold: '#C9A85B', leaf: '#6B9F4A', leafDeep: '#4E7A33', leafTint: 'rgba(107,159,74,0.14)',
  text: '#2A3528', textMuted: '#5C665C', textSoft: '#7A6B57', border: 'rgba(26,67,49,0.10)',
};
const money = (sym, n) => `${sym}${Number(n || 0).toFixed(2)}`;
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }); } catch { return ''; } };

const STAGES = [
  { key: 'ordered', label: 'Order placed', icon: Check, sub: "We've got your order" },
  { key: 'preparing', label: 'Preparing your order', icon: Package, sub: 'Being packed with care' },
  { key: 'shipped', label: 'Shipped', icon: Truck, sub: 'On its way to you' },
  { key: 'delivered', label: 'Delivered', icon: Home, sub: 'Enjoy — happy growing' },
];

function Timeline({ stageIndex }) {
  return (
    <div data-testid="order-timeline">
      {STAGES.map((s, i) => {
        const done = i < stageIndex;
        const current = i === stageIndex;
        const active = done || current;
        const isLast = i === STAGES.length - 1;
        const Icon = done ? Check : s.icon;
        return (
          <div key={s.key} className="flex gap-3">
            <div className="flex flex-col items-center" style={{ width: 28 }}>
              <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: active ? C.forest : C.sageDeep, border: current ? `3px solid ${C.leafTint}` : 'none' }}>
                <Icon className="w-3.5 h-3.5" style={{ color: active ? C.cream : C.textSoft }} />
              </div>
              {!isLast && <div className="w-0.5 flex-1" style={{ minHeight: 22, margin: '2px 0', background: done ? C.forest : C.sageDeep }} />}
            </div>
            <div className="flex-1" style={{ paddingBottom: isLast ? 0 : 16 }}>
              <p className="font-semibold" style={{ color: active ? C.forest : C.textSoft }}>{s.label}</p>
              <p className="text-sm" style={{ color: C.textSoft }}>{current ? s.sub : done ? 'Done' : 'Upcoming'}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DigitalRow() {
  return (
    <div className="flex items-center gap-3" data-testid="order-timeline">
      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: C.forest }}><Check className="w-3.5 h-3.5" style={{ color: C.cream }} /></div>
      <div className="flex-1">
        <p className="font-semibold" style={{ color: C.forest }}>Delivered to your Library</p>
        <p className="text-sm" style={{ color: C.textSoft }}>Instant — open it any time</p>
      </div>
      <Link to="/learn" data-testid="order-open-library"><ArrowUpRight className="w-[18px] h-[18px]" style={{ color: C.forest }} /></Link>
    </div>
  );
}

function OrderCard({ order: initial, index }) {
  const [order, setOrder] = useState(initial);
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const sym = order.currency_symbol || '$';
  const items = order.items || [];
  const count = items.reduce((a, it) => a + (it.quantity || 1), 0);
  const tracking = order.tracking || {};

  const apply = (d) => setOrder((o) => ({ ...o, ...d }));

  const save = async (stage) => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/shop/orders/${order.order_id}/tracking`, { tracking_url: url.trim() || undefined, stage }, { withCredentials: true });
      apply({ tracking: r.data.tracking, stage: r.data.stage, stage_index: r.data.stage_index, physical: r.data.physical, estimated_delivery: r.data.estimated_delivery });
      setUrl('');
    } catch { /* ignore */ } finally { setSaving(false); }
  };
  const refresh = async () => {
    setChecking(true);
    try {
      const r = await axios.post(`${API}/shop/orders/${order.order_id}/refresh-tracking`, {}, { withCredentials: true });
      apply({ stage: r.data.stage, stage_index: r.data.stage_index, estimated_delivery: r.data.estimated_delivery, tracking: r.data.tracking });
    } catch { /* ignore */ } finally { setChecking(false); }
  };
  const reorder = async () => {
    setBusy(true);
    try {
      const reItems = items.map((it) => ({ product_id: it.product_id, variant_id: it.variant_id, quantity: it.quantity || 1 })).filter((it) => it.product_id);
      const body = { items: reItems, origin_url: window.location.origin };
      if (order.physical && order.shipping_address) body.shipping_address = order.shipping_address;
      const r = await axios.post(`${API}/shop/checkout`, body, { withCredentials: true });
      if (r.data.url) window.location.href = r.data.url;
    } catch { /* ignore */ } finally { setBusy(false); }
  };

  useEffect(() => {
    if (order.stage !== 'shipped') return undefined;
    const t = setInterval(refresh, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.stage, order.order_id]);

  const eta = order.estimated_delivery ? new Date(order.estimated_delivery).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) : null;
  const carrier = tracking.carrier;

  return (
    <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index, 6) * 0.06, type: 'spring', stiffness: 220, damping: 24 }}
      data-testid={`order-card-${order.order_id}`} className="rounded-[20px] p-6" style={{ background: C.cream, boxShadow: '0 14px 40px -20px rgba(26,67,49,0.4)' }}>
      <div className="flex items-center gap-2 mb-4">
        <div className="flex-1">
          <p className="text-sm" style={{ color: C.textSoft }}>{fmtDate(order.created_at)}</p>
          <p className="font-semibold" style={{ color: C.forest }}>{count} item{count === 1 ? '' : 's'} · {money(sym, order.amount)}</p>
        </div>
        <span className="text-sm" style={{ color: C.textSoft }}>#{String(order.order_id || '').slice(-6)}</span>
      </div>

      <div className="mb-5 space-y-2">
        {items.slice(0, 3).map((it, k) => (
          <div key={k} className="flex items-center gap-2">
            <span className="w-[5px] h-[5px] rounded-full flex-shrink-0" style={{ background: C.gold }} />
            <span className="text-sm truncate" style={{ color: C.text }}>{it.name}{it.variant_label ? ` · ${it.variant_label}` : ''}{it.quantity > 1 ? ` ×${it.quantity}` : ''}</span>
          </div>
        ))}
      </div>

      {order.physical ? <Timeline stageIndex={order.stage_index ?? 1} /> : <DigitalRow />}

      {tracking.number || tracking.url ? (
        <div className="flex items-center gap-3 mt-3 rounded-2xl p-3" data-testid={`order-tracking-${order.order_id}`} style={{ background: C.leafTint }}>
          <Truck className="w-3.5 h-3.5 flex-shrink-0" style={{ color: C.forest }} />
          <div className="flex-1 min-w-0">
            <p className="text-sm" style={{ color: C.textSoft }}>{carrier || 'Courier'}</p>
            {tracking.number && <p className="font-semibold truncate" style={{ color: C.forest }}>{tracking.number}</p>}
            {order.stage === 'shipped' && eta && <p className="text-xs" style={{ color: C.textSoft }}>Arriving ~{eta}</p>}
          </div>
          {tracking.url && (
            <a href={tracking.url} target="_blank" rel="noreferrer" data-testid={`order-track-${order.order_id}`} className="inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm font-semibold flex-shrink-0" style={{ background: C.forest, color: C.cream }}>
              <ExternalLink className="w-3.5 h-3.5" /> Track parcel
            </a>
          )}
        </div>
      ) : order.physical && (
        <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t" style={{ borderColor: C.border }} data-testid={`tracking-${order.order_id}`}>
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Paste courier tracking link (https://…)" data-testid={`tracking-input-${order.order_id}`}
            className="flex-1 min-w-[200px] px-4 py-2 rounded-xl outline-none text-sm" style={{ background: '#FFFFFF', border: `1px solid ${C.border}` }} />
          <button onClick={() => save('shipped')} disabled={saving || !url.trim()} data-testid={`tracking-save-${order.order_id}`} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-50" style={{ background: C.forest, color: C.cream }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add tracking
          </button>
        </div>
      )}

      {order.physical && order.stage === 'shipped' && (
        <div className="flex items-center gap-3 flex-wrap mt-2">
          <button onClick={refresh} disabled={checking} data-testid={`refresh-tracking-${order.order_id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: C.forest }}>
            <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} /> Check for updates
          </button>
          <button onClick={() => save('delivered')} disabled={saving} data-testid={`mark-delivered-${order.order_id}`} className="text-sm font-semibold" style={{ color: C.leaf }}>Mark as delivered</button>
        </div>
      )}
      {order.physical && order.stage === 'delivered' && (
        <p className="flex items-center gap-1.5 text-sm font-semibold mt-2" style={{ color: C.leaf }}><CheckCircle2 className="w-4 h-4" /> Delivered</p>
      )}

      <button onClick={reorder} disabled={busy} data-testid={`order-reorder-${order.order_id}`} className="w-full mt-4 h-12 rounded-full flex items-center justify-center gap-2 font-semibold transition-transform active:scale-[0.98]" style={{ border: `1.5px solid ${C.forest}`, background: C.creamWarm, color: C.forest }}>
        <RotateCcw className="w-[15px] h-[15px]" /> {busy ? 'Starting…' : 'Reorder'}
      </button>

      {order.shipping_address?.line1 && (
        <p className="text-xs mt-3" style={{ color: C.textSoft }}>Shipping to {order.shipping_address.name}, {order.shipping_address.line1}, {order.shipping_address.city}</p>
      )}
    </motion.div>
  );
}

export default function Orders({ user }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/shop/orders`, { withCredentials: true }).then((r) => setOrders(Array.isArray(r.data) ? r.data : [])).catch(() => setOrders([])).finally(() => setLoading(false));
  }, []);

  return (
    <Layout user={user}>
      <div className="space-y-5 max-w-2xl">
        <div>
          {FEATURES.shop ? <Link to="/shop" className="inline-flex items-center gap-1 text-sm font-semibold mb-2" style={{ color: C.textSoft }}><ChevronLeft className="w-4 h-4" /> Back to shop</Link> : <Link to="/today" className="inline-flex items-center gap-1 text-sm font-semibold mb-2" style={{ color: C.textSoft }}><ChevronLeft className="w-4 h-4" /> Back to today</Link>}
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }}>My orders</h1>
          <p className="mt-1" style={{ color: C.textMuted }}>Track your deliveries and digital purchases.</p>
        </div>

        {loading ? (
          <div className="space-y-4">{Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-48 rounded-[20px] animate-pulse" style={{ background: 'rgba(255,255,255,0.6)' }} />)}</div>
        ) : orders.length === 0 ? (
          <div className="rounded-3xl p-12 text-center" data-testid="orders-empty" style={{ background: C.cream }}>
            <ShoppingBag className="w-12 h-12 mx-auto mb-3" style={{ color: C.sageDeep }} />
            <p className="mb-4" style={{ color: C.textSoft }}>Your shop orders and their delivery progress will appear here.</p>
            {FEATURES.shop ? <Link to="/shop" className="inline-block rounded-full px-6 py-3 font-semibold" style={{ background: C.forest, color: C.cream }}>Browse the shop</Link> : null}
          </div>
        ) : (
          <div className="space-y-5">
            {orders.map((o, i) => <OrderCard key={o.order_id} order={o} index={i} />)}
          </div>
        )}
      </div>
    </Layout>
  );
}
